import { commitLocalUpdate } from 'relay-runtime'
import type { IEnvironment, RecordProxy, RecordSourceProxy } from 'relay-runtime'

import { startTransfer, startAlbumTransfer } from '../Daemon/Transfer'
import type { AlbumVideo } from '../Daemon/Transfer'
import { options as fulfillmentOptions } from '../components/FulfillmentOptions'
import type { OrderDetails_order } from '../__generated__/OrderDetails_order.graphql'
import type { TransferProgress } from '../Daemon/Transfer'
import { getAlbumVideos } from './Downloader'

export interface LocalDevice {
  id: string
  status: string
  videoTransferStatus: string
  videoPercentageTransferred: string
  transferredOrderId: string
  allowBlank?: boolean
}

interface Order extends OrderDetails_order {}

// The video we load onto books our customer's will load themselves.
// Also in shopify.go.
export const BLANK_VIDEO = "https://public.sendheirloom.com/DELETE_ME.mp4"

export function getOrder(store: RecordSourceProxy, orderId: string): (RecordProxy|null) {
  return store.get(orderId) || null
}

// It would be nice if we could just use the relay vars for this, but they update async
let orderStatuses = new Map()
let deviceStatuses = new Map()

// Build the video array for the daemon transfer. For album orders, each page's
// video becomes an entry. For legacy orders, the single card video is page 1.
function buildVideoArray(order: Order): AlbumVideo[] {
  const albumVideos = getAlbumVideos(order)
  if (albumVideos.length > 0) {
    return albumVideos.map(v => ({
      page_number: v.pageNumber,
      hash_sha256: v.sha256,
    }))
  }

  // Legacy order: single video as page 1
  if (order.cardVideoSHA256) {
    return [{ page_number: 1, hash_sha256: order.cardVideoSHA256 }]
  }

  return []
}

function startLoading(environment: IEnvironment, device: LocalDevice, order: Order){
  const videos = buildVideoArray(order)
  if (videos.length === 0) return

  const updateVal = function (store: RecordSourceProxy, key: string, val: number|string){
    let deviceRef = store.get("device:" + device.id)
    if (deviceRef) {
      deviceRef.setValue(val, key)
    }

    let orderRef = getOrder(store, order.id)
    if (orderRef) {
      orderRef.setValue(val, key)
    }

    if (key === 'videoTransferStatus'){
      orderStatuses.set(order.id, val)
      deviceStatuses.set(device.id, val)
    }
  }

  commitLocalUpdate(environment, store => {
    updateVal(store, 'videoTransferStatus', 'transferring')
    updateVal(store, 'videoPercentageTransferred', 0)
    updateVal(store, 'transferredOrderId', "")
  })

  const { formatBooks, verifyTransfers } = fulfillmentOptions

  const isAlbum = videos.length > 1 || (videos.length === 1 && !order.cardVideoSHA256)
  const progressCb = (p: TransferProgress) => {
    commitLocalUpdate(environment, store => {
      updateVal(store, 'videoPercentageTransferred', p.percent_complete)
    })
  }

  // Use legacy single-file transfer for regular video books, album transfer only for album orders
  const res = isAlbum
    ? startAlbumTransfer(device.id, videos, formatBooks(), verifyTransfers(), progressCb)
    : startTransfer(device.id, order.cardVideoSHA256!, formatBooks(), verifyTransfers(), progressCb)

  res.then(() => {
    commitLocalUpdate(environment, store => {
      updateVal(store, 'videoTransferStatus', 'transferred')
      updateVal(store, 'transferredOrderId', order.id)
    })
  }, (err) => {
    commitLocalUpdate(environment, store => {
      console.error("Error transferring video", err)

      if (err?.message === "Entry not downloaded"){
        // The daemon was likely restarted between when this was downloaded and when it was transferred
        let orderRef = getOrder(store, order.id)
        orderRef?.setValue(null, 'videoDownloadStatus')

        updateVal(store, 'videoTransferStatus', '')
      } else {
        updateVal(store, 'videoTransferStatus', 'error')
      }
    })
  })
}

function findReadyOrder(orders: Order[], allowBlank: boolean): Order | null{
  for (let i=orders.length; i--;){
    if (!orders[i] || !orders[i].shopify) {
      continue
    }
    if (orders[i].shopify!.videoManifest &&
        orders[i].shopify!.videoManifest!.length === 1 &&
        orders[i].shopify!.videoManifest![0] === BLANK_VIDEO &&
        !allowBlank) {
      console.log("Skipping device as video is blank and device does not support customer loading.")
      continue
    }

    let transferStatus = orderStatuses.get(orders[i].id)
    if (orders[i].videoDownloadStatus === "downloaded" && !transferStatus){
      return orders[i]
    }
  }

  return null
}

export function updateDevices(environment: IEnvironment, orders: Order[], devices: LocalDevice[]){
  if (!devices)
    return

  devices.forEach(device => {
    let transferStatus = deviceStatuses.get(device.id)
    if (device.status === 'active' && !transferStatus){
      let readyOrder = findReadyOrder(orders, device.allowBlank ?? false)
      if (readyOrder){
        startLoading(environment, device, readyOrder)
      }
    }
  })

}
